import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileUp, X } from "lucide-react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { DsButton } from "../../../design-system";
import { salaryService, payrollOpsService } from "../../../services/api";
import {
  ImportValidationReview,
  buildImportValidationResult,
  downloadValidationStatusWorkbook,
} from "../../Common/import-validation";
import { structureAppliesToEmployee } from "../../../utils/salaryStructureMatch";
import {
  listManualFixedComponents,
  manualFixedColumnLabel,
  structureRequiresManualInput,
  structureCalcModeLabel,
  buildEmployeeStructureSavePayload,
} from "../../../utils/applyEmployeeStructureFromTemplate";

export const SALARY_REVISION_REASONS = [
  "Performance",
  "Promotion",
  "As per benchmarking",
  "Market adjustment",
  "Retention",
  "Skill enhancement",
  "Other",
];

export const REVISION_TYPES = ["Amount", "%age"];

/** PaceHRM Excel date format for all import templates. */
export const EXCEL_DATE_FORMAT = "dd/mm/yyyy";
export const EXCEL_MONTH_FORMAT = "yyyy-mm";
export const CONTINUE_YES_NO = ["No", "Yes"];

function currentYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function autoHeaders(allowMidMonth) {
  return [
    "Employee Code",
    "Employee Name",
    "Current CTC",
    "Revision Type",
    "Revision Value",
    allowMidMonth ? "Increment Effective Date" : "Increment Effective Month",
    "Pay Month",
    "Continue Same Month Increment",
    "Reason",
    "Remarks",
  ];
}

function parsePayMonth(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
  }
  const s = String(value).trim();
  // Excel date serial (Pay Month cells sometimes lose @ text format)
  if (/^\d+(\.\d+)?$/.test(s) && Number(s) > 20000 && Number(s) < 80000) {
    const serial = Math.floor(Number(s));
    const utc = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
    return utc.toISOString().slice(0, 7);
  }
  const iso = parseExcelDate(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso.slice(0, 7);
  const ym = s.match(/^(\d{4})-(\d{2})$/);
  if (ym) return `${ym[1]}-${ym[2]}`;
  const mmyyyy = s.match(/^(\d{1,2})[/-](\d{4})$/);
  if (mmyyyy) {
    const month = Number(mmyyyy[1]);
    if (month >= 1 && month <= 12) {
      return `${mmyyyy[2]}-${String(month).padStart(2, "0")}`;
    }
  }
  return "";
}

function parseContinueYes(value) {
  const s = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!s) return false;
  return ["yes", "y", "true", "1"].includes(s);
}

function normalizeHeader(h) {
  return String(h || "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\s*\([^)]*\)\s*$/g, "")
    .replace(/\*+$/g, "")
    .trim();
}

/** Coerce Excel cell values (plain, rich text, formula result) to a trimmed string. */
function cellToString(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value).trim();
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "object") {
    if (value.text != null) return String(value.text).trim();
    if (Array.isArray(value.richText)) {
      return value.richText.map((p) => p?.text || "").join("").trim();
    }
    if (value.result != null) return cellToString(value.result);
    if (value.w != null) return String(value.w).trim();
    if (value.v != null) return cellToString(value.v);
  }
  return String(value).trim();
}

function parseNumber(value) {
  if (value == null || value === "") return NaN;
  return Number(String(value).replace(/,/g, "").trim());
}

function parseExcelDate(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const s = String(value).trim();
  if (/^\d+(\.\d+)?$/.test(s) && Number(s) > 20000 && Number(s) < 80000) {
    const serial = Math.floor(Number(s));
    const utc = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
    return utc.toISOString().slice(0, 10);
  }
  const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${String(dmy[2]).padStart(2, "0")}-${String(dmy[1]).padStart(2, "0")}`;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const ym = s.match(/^(\d{4})-(\d{2})$/);
  if (ym) return `${ym[1]}-${ym[2]}-01`;
  return s;
}

function normalizeRevisionType(raw) {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
  if (!s) return "";
  if (["amount", "amt", "absolute", "ctc", "revisedctc"].includes(s)) {
    return "Amount";
  }
  if (
    ["%age", "%", "percent", "percentage", "pct", "hike%", "hike"].includes(s) ||
    s.includes("%")
  ) {
    return "%age";
  }
  return String(raw).trim();
}

function resolveRevisedCtc({ revisionType, revisionValue, legacyNewCtc, currentCtc }) {
  if (revisionType === "%age") {
    if (!Number.isFinite(revisionValue)) {
      return { newCtc: NaN, error: "Revision Value must be a number for %age" };
    }
    if (revisionValue <= -100) {
      return { newCtc: NaN, error: "%age must be greater than -100" };
    }
    if (!Number.isFinite(currentCtc) || currentCtc <= 0) {
      return {
        newCtc: NaN,
        error: "Current CTC is required to compute %age revision (fill Current CTC or use Amount)",
      };
    }
    const newCtc = Math.round(currentCtc * (1 + revisionValue / 100) * 100) / 100;
    return { newCtc, error: null };
  }

  if (revisionType === "Amount") {
    if (!Number.isFinite(revisionValue)) {
      return {
        newCtc: NaN,
        error: "Revision Value must be a number for Amount (increment to add to Current CTC)",
      };
    }
    if (!Number.isFinite(currentCtc) || currentCtc <= 0) {
      return {
        newCtc: NaN,
        error: "Current CTC is required to compute Amount revision (fill Current CTC)",
      };
    }
    const newCtc = Math.round((currentCtc + revisionValue) * 100) / 100;
    if (newCtc <= 0) {
      return {
        newCtc: NaN,
        error: "Revised CTC must be positive (Current CTC + Amount)",
      };
    }
    return { newCtc, error: null };
  }

  if (Number.isFinite(legacyNewCtc) && legacyNewCtc > 0) {
    return { newCtc: legacyNewCtc, error: null };
  }

  return {
    newCtc: NaN,
    error: "Revision Type must be Amount or %age, with a Revision Value",
  };
}

function mapAutoRowFromHeaders(headers, row, rowNumber, currentCtcByCode = new Map()) {
  // Prefer exact header match; only fall back to includes for multi-word aliases
  // (avoids short tokens like "type" / "value" / "name" grabbing the wrong column).
  const idx = (aliases) => {
    const norms = aliases.map((a) => normalizeHeader(a)).filter(Boolean);
    for (let i = 0; i < headers.length; i++) {
      const h = normalizeHeader(headers[i]);
      if (norms.some((a) => h === a)) return i;
    }
    for (let i = 0; i < headers.length; i++) {
      const h = normalizeHeader(headers[i]);
      if (
        norms.some(
          (a) => a.length >= 4 && (h.includes(a) || a.includes(h))
        )
      ) {
        return i;
      }
    }
    return -1;
  };
  const get = (aliases) => {
    const i = idx(aliases);
    return i >= 0 ? row[i] : "";
  };

  const empCode = cellToString(get(["employee code", "emp code", "empcode"]));
  const excelCurrent = parseNumber(get(["current ctc"]));
  const lookupCurrent = currentCtcByCode.get(empCode.toUpperCase());
  const currentCtc =
    Number.isFinite(excelCurrent) && excelCurrent > 0
      ? excelCurrent
      : Number.isFinite(lookupCurrent) && lookupCurrent > 0
        ? lookupCurrent
        : NaN;

  const revisionType = normalizeRevisionType(
    get(["revision type", "revision mode", "hike type"])
  );
  const revisionValue = parseNumber(
    get(["revision value", "hike value", "hike amount"])
  );
  const legacyNewCtc = parseNumber(
    get(["revised annual ctc", "revised ctc", "new ctc"])
  );

  const resolved = resolveRevisedCtc({
    revisionType,
    revisionValue,
    legacyNewCtc,
    currentCtc,
  });

  return {
    kind: "auto",
    rowNumber,
    empCode,
    employeeName: cellToString(get(["employee name"])),
    currentCtc: Number.isFinite(currentCtc) ? currentCtc : null,
    revisionType: revisionType || (Number.isFinite(legacyNewCtc) ? "Amount" : ""),
    revisionValue: Number.isFinite(revisionValue)
      ? revisionValue
      : Number.isFinite(legacyNewCtc)
        ? legacyNewCtc
        : null,
    newCtc: resolved.newCtc,
    resolveError: resolved.error,
    effectiveDate: parseExcelDate(
      get([
        "increment effective date",
        "increment effective month",
        "effective date",
        "effective from",
      ])
    ),
    payMonth: parsePayMonth(
      get(["pay month", "booking pay month", "payroll month", "booking month"])
    ),
    continueSameMonthIncrement: parseContinueYes(
      get([
        "continue same month increment",
        "continue same month",
        "confirm same effective continue",
      ])
    ),
    reason: cellToString(get(["reason"])),
    remarks: cellToString(get(["remarks", "remark", "notes"])) || null,
    manualOverrides: null,
    structureSnapshot: null,
  };
}

function mapManualRowFromHeaders(
  headers,
  row,
  rowNumber,
  manualComponents,
  currentCtcByCode = new Map()
) {
  const obj = {};
  headers.forEach((h, i) => {
    obj[normalizeHeader(h)] = row[i];
  });

  const empCode = cellToString(
    obj["employee code"] ?? obj["emp code"] ?? obj.empcode ?? ""
  ).toUpperCase();
  const employeeName = cellToString(obj["employee name"] ?? obj.name ?? "");
  const excelCurrent = parseNumber(obj["current ctc"]);
  const lookupCurrent = currentCtcByCode.get(empCode);
  const currentCtc =
    Number.isFinite(excelCurrent) && excelCurrent > 0
      ? excelCurrent
      : Number.isFinite(lookupCurrent) && lookupCurrent > 0
        ? lookupCurrent
        : NaN;

  const newCtc = parseNumber(
    obj["revised annual ctc"] ?? obj["revised ctc"] ?? obj["new ctc"]
  );

  const overrides = {};
  const amountErrors = [];
  (manualComponents || []).forEach((comp) => {
    const label = manualFixedColumnLabel(comp);
    const key = normalizeHeader(label);
    const rawVal = obj[key];
    if (rawVal == null || cellToString(rawVal) === "") {
      amountErrors.push(`${label} is required`);
      return;
    }
    const n = parseNumber(rawVal);
    if (!Number.isFinite(n) || n < 0) {
      amountErrors.push(`${label} must be a monthly amount ≥ 0`);
      return;
    }
    overrides[comp.name] = n;
    if (comp.id != null) overrides[String(comp.id)] = n;
    if (comp.code) overrides[String(comp.code)] = n;
  });

  return {
    kind: "manual",
    rowNumber,
    empCode,
    employeeName,
    currentCtc: Number.isFinite(currentCtc) ? currentCtc : null,
    revisionType: "Manual",
    revisionValue: null,
    newCtc,
    resolveError:
      Number.isFinite(newCtc) && newCtc > 0
        ? null
        : "Revised Annual CTC must be a positive number",
    effectiveDate: parseExcelDate(
      obj["increment effective date"] ??
        obj["increment effective month"] ??
        obj["effective date"] ??
        obj["effective from"] ??
        ""
    ),
    payMonth: parsePayMonth(
      obj["pay month"] ??
        obj["booking pay month"] ??
        obj["payroll month"] ??
        obj["booking month"] ??
        ""
    ),
    continueSameMonthIncrement: parseContinueYes(
      obj["continue same month increment"] ??
        obj["continue same month"] ??
        obj["confirm same effective continue"] ??
        ""
    ),
    // normalizeHeader strips trailing * so Reason* maps to "reason"
    reason: cellToString(obj.reason ?? ""),
    remarks: cellToString(obj.remarks ?? obj.remark ?? obj.notes ?? "") || null,
    manualOverrides: overrides,
    amountErrors,
  };
}

function clientValidateAuto(row, allowMidMonth, finalizedSet = new Set()) {
  const errors = [];
  if (!row.empCode) errors.push("Employee Code is required");
  if (row.resolveError) errors.push(row.resolveError);
  else if (!Number.isFinite(row.newCtc) || row.newCtc <= 0) {
    errors.push("Resolved Revised CTC must be a positive number");
  } else if (
    Number.isFinite(row.currentCtc) &&
    Math.abs(Number(row.newCtc) - Number(row.currentCtc)) < 1
  ) {
    errors.push("Revised CTC must differ from Current CTC (No Change revisions are blocked)");
  }
  if (!row.effectiveDate || !/^\d{4}-\d{2}-\d{2}$/.test(row.effectiveDate)) {
    errors.push(
      allowMidMonth
        ? `Increment Effective Date must be ${EXCEL_DATE_FORMAT} (e.g. 01/07/2026)`
        : `Increment Effective Month must be ${EXCEL_MONTH_FORMAT} (e.g. 2026-07)`
    );
  } else if (!allowMidMonth && row.effectiveDate.slice(8, 10) !== "01") {
    errors.push("Effective month must be the 1st of the month (mid-month increments are off)");
  }
  const payYm = String(row.payMonth || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(payYm)) {
    errors.push(`Pay Month is required (${EXCEL_MONTH_FORMAT}, e.g. 2026-07)`);
  } else {
    const effYm = String(row.effectiveDate || "").slice(0, 7);
    if (/^\d{4}-\d{2}$/.test(effYm) && payYm < effYm) {
      errors.push("Pay Month cannot be before Increment Effective month");
    }
    if (finalizedSet.has(payYm)) {
      errors.push(`Pay Month ${payYm} is payroll-finalised — use the same or a later unfinalised month`);
    }
  }
  const reasonNorm = String(row.reason || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!reasonNorm) errors.push("Reason is required");
  else if (
    !SALARY_REVISION_REASONS.some(
      (r) => r.toLowerCase() === reasonNorm.toLowerCase()
    )
  ) {
    errors.push(`Reason must be one of: ${SALARY_REVISION_REASONS.join(", ")}`);
  }
  return errors;
}

function clientValidateManualBase(row, allowMidMonth, finalizedSet = new Set()) {
  const errors = clientValidateAuto(row, allowMidMonth, finalizedSet);
  (row.amountErrors || []).forEach((e) => errors.push(e));
  return errors;
}

function pickRevisionsSheetName(sheetNames = []) {
  const names = Array.isArray(sheetNames) ? sheetNames : [];
  const exact = names.find((n) => String(n).trim().toLowerCase() === "revisions");
  if (exact) return exact;
  const helper = /^(instructions?|reasons?|revision\s*types?|allowed\s*types?|types?)$/i;
  const soft = names.find(
    (n) => /revision/i.test(String(n)) && !helper.test(String(n).trim())
  );
  if (soft) return soft;
  const nonHelper = names.find((n) => !helper.test(String(n).trim()));
  return nonHelper || names[0] || null;
}

function employeeDisplayName(emp) {
  return (
    [emp?.user?.name, emp?.user?.lastName, emp?.firstName, emp?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    emp?.name ||
    ""
  );
}

function isActiveEmployment(emp) {
  const s = String(emp?.employmentStatus || "").trim();
  return s !== "Relieved" && s !== "Terminated";
}

function headersLookManual(headers = []) {
  const norms = headers.map(normalizeHeader);
  const hasRevisionType = norms.some((h) => h === "revision type" || h.includes("revision type"));
  const hasRevisedCtc = norms.some(
    (h) =>
      h.includes("revised annual ctc") ||
      h === "revised ctc" ||
      h === "new ctc"
  );
  return !hasRevisionType && hasRevisedCtc;
}

/**
 * Bulk Salary Revision import.
 * Select a salary structure — product detects Manual vs Auto (Formula) and
 * downloads / validates the matching template (same pattern as Structure component values).
 */
export default function SalaryRevisionImportModal({
  isOpen,
  onClose,
  darkMode = false,
  employees = [],
  structures = [],
  statutoryConfigJson = null,
  branchesList = [],
  allowMidMonthIncrements = false,
  bookingPayMonth = null,
  onImported,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [sourceFile, setSourceFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [review, setReview] = useState(null);
  const [finalizedPayrollMonthSet, setFinalizedPayrollMonthSet] = useState(
    () => new Set()
  );

  const allowMidMonth = !!allowMidMonthIncrements;
  const defaultPayMonth = bookingPayMonth
    ? String(bookingPayMonth).slice(0, 7)
    : currentYm();

  const selectedStructure = useMemo(
    () => (structures || []).find((s) => String(s.id) === String(templateId)) || null,
    [structures, templateId]
  );

  const isManualStructure = structureRequiresManualInput(selectedStructure);
  const structureModeLabel = selectedStructure
    ? structureCalcModeLabel(selectedStructure)
    : "";

  const manualComponents = useMemo(
    () => listManualFixedComponents(selectedStructure),
    [selectedStructure]
  );

  const manualHeaders = useMemo(
    () => [
      "Employee Code*",
      "Employee Name",
      "Current CTC",
      ...manualComponents.map((c) => manualFixedColumnLabel(c)),
      "Revised Annual CTC*",
      allowMidMonth ? "Increment Effective Date*" : "Increment Effective Month*",
      "Pay Month*",
      "Continue Same Month Increment",
      "Reason*",
      "Remarks",
    ],
    [manualComponents, allowMidMonth]
  );

  const currentCtcByCode = useMemo(() => {
    const map = new Map();
    (employees || []).forEach((emp) => {
      const code = String(emp.empCode || emp.employeeCode || "")
        .trim()
        .toUpperCase();
      if (!code) return;
      const ctc = Number(emp.ctc || emp.ctcAmount || 0);
      if (ctc > 0) map.set(code, ctc);
    });
    return map;
  }, [employees]);

  const employeesByCode = useMemo(() => {
    const map = new Map();
    (employees || []).forEach((emp) => {
      const code = String(emp.empCode || emp.employeeCode || "")
        .trim()
        .toUpperCase();
      if (code) map.set(code, emp);
    });
    return map;
  }, [employees]);

  const seedEmployees = useMemo(() => {
    if (!selectedStructure) return [];
    return (employees || []).filter((emp) => {
      if (!isActiveEmployment(emp)) return false;
      return structureAppliesToEmployee(selectedStructure.assignment || {}, emp);
    });
  }, [employees, selectedStructure]);

  useEffect(() => {
    if (!isOpen) {
      setReview(null);
      setPreviewRows([]);
      setImportResult(null);
      setSourceFile(null);
      setTemplateId("");
      return undefined;
    }
    let cancelled = false;
    payrollOpsService
      .listFinalizedPayrollMonths()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        const set = new Set(
          list
            .map((m) => String(m?.payMonth || m?.month || m || "").slice(0, 7))
            .filter((ym) => /^\d{4}-\d{2}$/.test(ym))
        );
        setFinalizedPayrollMonthSet(set);
      })
      .catch(() => {
        if (!cancelled) setFinalizedPayrollMonthSet(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const buildReviewFromPreview = (mapped, isManual, structure) => {
    const validRecords = [];
    const invalidRecords = [];

    mapped.forEach((r) => {
      const record = {
        "Employee Code": r.empCode,
        "Employee Name": r.employeeName || r.name || "",
        Name: r.employeeName || r.name || r.empCode,
        ...r,
      };

      if (r.clientErrors?.length) {
        invalidRecords.push({
          row: r.rowNumber,
          record,
          error: r.clientErrors.join("; "),
          fieldErrors: { Validation: r.clientErrors.join("; ") },
        });
        return;
      }

      if (structure) {
        const emp = employeesByCode.get(String(r.empCode || "").toUpperCase());
        if (!emp) {
          invalidRecords.push({
            row: r.rowNumber,
            record,
            error: "Employee Code not found in this company",
            fieldErrors: { "Employee Code": "Employee Code not found in this company" },
          });
          return;
        }
        if (!structureAppliesToEmployee(structure.assignment || {}, emp)) {
          invalidRecords.push({
            row: r.rowNumber,
            record,
            error: "Employee is not in this salary structure assignment group",
            fieldErrors: {
              Structure: "Employee is not in this salary structure assignment group",
            },
          });
          return;
        }

        if (isManual) {
          const built = buildEmployeeStructureSavePayload({
            structure,
            employee: { ...emp, ctc: r.newCtc },
            overrides: r.manualOverrides || {},
            statutoryConfigJson,
            branchesList,
            effectiveFrom: r.effectiveDate,
            changeNote: `Salary revision Manual import: ${structure.name}`,
          });
          if (!built.ok) {
            invalidRecords.push({
              row: r.rowNumber,
              record,
              error: built.message || built.reason || "Manual amounts do not fit Revised CTC",
              fieldErrors: {
                Validation:
                  built.message || built.reason || "Manual amounts do not fit Revised CTC",
              },
            });
            return;
          }
          validRecords.push({
            ...r,
            __row: r.rowNumber,
            structureSnapshot: built.payload,
            record: { ...record, structureSnapshot: built.payload },
          });
          return;
        }
      }

      validRecords.push({ ...r, __row: r.rowNumber, record });
    });

    return buildImportValidationResult({ validRecords, invalidRecords });
  };

  if (!isOpen || typeof document === "undefined") return null;

  const downloadAutoTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = "PaceHRM";
    wb.created = new Date();
    const headers = autoHeaders(allowMidMonth);
    const effectiveHint = allowMidMonth
      ? `Increment Effective Date format: ${EXCEL_DATE_FORMAT} (example: 01/07/2026). Any calendar day is allowed.`
      : `Increment Effective Month format: ${EXCEL_MONTH_FORMAT} (example: 2026-07). Mid-month is off — stored as the 1st.`;

    const info = wb.addWorksheet("Instructions");
    info.getColumn(1).width = 108;
    const lines = [
      "Salary Revision Bulk Import — Auto (Formula) / CTC Amount/%age",
      "",
      selectedStructure ? `Structure: ${selectedStructure.name} (Auto / Formula)` : "",
      "",
      "1. Use the Revisions sheet only. Do not rename column headers.",
      "2. Employee Code must match PaceHRM exactly (e.g. MK0013).",
      "3. Revision Type must be Amount or %age (dropdown on Revisions sheet).",
      "4. Revision Value:",
      "   - Amount → enter the increment amount to ADD to Current CTC (e.g. 25000).",
      "   - %age → enter the hike percentage (e.g. 25 for 25% increase).",
      "   Revised CTC = Current CTC + Amount, or Current CTC × (1 + % / 100).",
      `5. ${effectiveHint}`,
      `6. Pay Month (${EXCEL_MONTH_FORMAT}) is the open payroll cycle that receives the increment and arrears.`,
      "   It must be the same as Increment Effective month, or a later unfinalised month — never an earlier month.",
      "   Example: Increment Effective July + Pay Month September → September pays new CTC plus Jul–Aug differentials.",
      "7. Continue Same Month Increment: No (default) fails if that effective month already has a revision.",
      "   Yes = additional increment on current CTC (same as Continue on the Salary Revision screen).",
      "   You may add a second row for the same employee in this file if Continue = Yes.",
      `8. Reason must be one of: ${SALARY_REVISION_REASONS.join(" | ")}.`,
      "9. Breakdown rebuilds from structure formulas after import (no Manual component columns).",
      "10. Save as .xlsx and upload from this screen. Download a fresh template after payroll settings change.",
    ].filter((t, i, arr) => !(t === "" && arr[i - 1] === ""));
    lines.forEach((t, i) => {
      const cell = info.getCell(i + 1, 1);
      cell.value = t;
      if (i === 0) cell.font = { bold: true, size: 14 };
    });

    const reasonsSheet = wb.addWorksheet("Reasons");
    reasonsSheet.getColumn(1).width = 28;
    reasonsSheet.getCell(1, 1).value = "Allowed Reasons";
    reasonsSheet.getCell(1, 1).font = { bold: true };
    SALARY_REVISION_REASONS.forEach((r, i) => {
      reasonsSheet.getCell(i + 2, 1).value = r;
    });

    const typesSheet = wb.addWorksheet("AllowedTypes");
    typesSheet.getColumn(1).width = 16;
    typesSheet.getCell(1, 1).value = "Revision Type";
    typesSheet.getCell(1, 1).font = { bold: true };
    REVISION_TYPES.forEach((t, i) => {
      typesSheet.getCell(i + 2, 1).value = t;
    });

    const yesNoSheet = wb.addWorksheet("YesNo");
    yesNoSheet.getColumn(1).width = 10;
    yesNoSheet.getCell(1, 1).value = "Continue";
    yesNoSheet.getCell(1, 1).font = { bold: true };
    CONTINUE_YES_NO.forEach((t, i) => {
      yesNoSheet.getCell(i + 2, 1).value = t;
    });

    const sheet = wb.addWorksheet("Revisions");
    const widths = [16, 24, 14, 14, 16, 28, 14, 30, 22, 28];
    headers.forEach((h, i) => {
      const cell = sheet.getCell(1, i + 1);
      cell.value = h;
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE8EEF5" },
      };
      sheet.getColumn(i + 1).width = widths[i] || 16;
    });

    const sampleEffective = allowMidMonth ? "01/07/2026" : "2026-07";
    const seed = seedEmployees.slice(0, 500);
    if (seed.length) {
      seed.forEach((emp, idx) => {
        const r = idx + 2;
        sheet.getCell(r, 1).value = emp.empCode || emp.employeeCode || "";
        sheet.getCell(r, 2).value = employeeDisplayName(emp);
        sheet.getCell(r, 3).value = Number(emp.ctc || emp.ctcAmount || 0) || null;
        sheet.getCell(r, 4).value = "Amount";
        sheet.getCell(r, 5).value = null;
        sheet.getCell(r, 6).value = "";
        sheet.getCell(r, 6).numFmt = "@";
        sheet.getCell(r, 7).value = defaultPayMonth;
        sheet.getCell(r, 7).numFmt = "@";
        sheet.getCell(r, 8).value = "No";
        sheet.getCell(r, 9).value = "";
        sheet.getCell(r, 10).value = "";
      });
    } else {
      sheet.getCell(2, 1).value = "MK0001";
      sheet.getCell(2, 2).value = "Sample Employee";
      sheet.getCell(2, 3).value = 500000;
      sheet.getCell(2, 4).value = "Amount";
      sheet.getCell(2, 5).value = 50000;
      sheet.getCell(2, 6).value = sampleEffective;
      sheet.getCell(2, 6).numFmt = "@";
      sheet.getCell(2, 7).value = defaultPayMonth;
      sheet.getCell(2, 7).numFmt = "@";
      sheet.getCell(2, 8).value = "No";
      sheet.getCell(2, 9).value = "Performance";
      sheet.getCell(2, 10).value = "";
    }

    const lastDataRow = Math.max(3, seed.length + 1);
    for (let r = 2; r <= Math.max(lastDataRow, 200); r++) {
      sheet.getCell(r, 4).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`AllowedTypes!$A$2:$A$${REVISION_TYPES.length + 1}`],
        showErrorMessage: true,
        errorTitle: "Invalid Revision Type",
        error: "Select Amount or %age",
      };
      sheet.getCell(r, 6).numFmt = "@";
      sheet.getCell(r, 7).numFmt = "@";
      sheet.getCell(r, 8).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`YesNo!$A$2:$A$${CONTINUE_YES_NO.length + 1}`],
        showErrorMessage: true,
        errorTitle: "Invalid value",
        error: "Select Yes or No",
      };
      sheet.getCell(r, 9).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`Reasons!$A$2:$A$${SALARY_REVISION_REASONS.length + 1}`],
        showErrorMessage: true,
        errorTitle: "Invalid Reason",
        error: "Select a reason from the list",
      };
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Salary_Revision_${
      selectedStructure
        ? String(selectedStructure.name || "Auto").replace(/[^\w.-]+/g, "_").slice(0, 40)
        : "Auto"
    }.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadManualTemplate = async () => {
    if (!selectedStructure) {
      toast.error("Select a salary structure first");
      return;
    }
    if (!manualComponents.length) {
      toast.error("Selected structure has no Manual fixed components");
      return;
    }

    const wb = new ExcelJS.Workbook();
    wb.creator = "PaceHRM";
    wb.created = new Date();
    const effectiveHint = allowMidMonth
      ? `Increment Effective Date* format: ${EXCEL_DATE_FORMAT} (example: 01/07/2026). Mid-month is enabled.`
      : `Increment Effective Month* format: ${EXCEL_MONTH_FORMAT} (example: 2026-07). Mid-month is off.`;

    const info = wb.addWorksheet("Instructions");
    info.getColumn(1).width = 108;
    const lines = [
      "Salary Revision Bulk Import — Manual component amounts",
      "",
      `Structure: ${selectedStructure.name}`,
      "",
      "1. Use the Revisions sheet only. Do not rename headers.",
      "2. Employee Code must match PaceHRM exactly.",
      "3. Fill monthly amounts for each Manual fixed component column (0 is allowed; blank fails validation).",
      "4. Revised Annual CTC* must match the component totals (Balance CTC fills if configured).",
      "5. Formula / Balance CTC columns are not entered — system calculates them.",
      `6. ${effectiveHint}`,
      `7. Pay Month* (${EXCEL_MONTH_FORMAT}) = open payroll cycle for increment + arrears. Same month as effective, or later unfinalised only.`,
      "8. Continue Same Month Increment: Yes stacks another increment on current CTC when that effective month already has a revision.",
      `9. Reason* must be one of: ${SALARY_REVISION_REASONS.join(" | ")}.`,
      "10. System never auto-scales Manual amounts by CTC ratio — enter the new monthly ₹ (including 0).",
      "11. Same employee may appear on more than one row if Continue = Yes. Save as .xlsx and upload from this screen.",
    ];
    lines.forEach((t, i) => {
      const cell = info.getCell(i + 1, 1);
      cell.value = t;
      if (i === 0) cell.font = { bold: true, size: 14 };
    });

    const reasonsSheet = wb.addWorksheet("Reasons");
    reasonsSheet.getColumn(1).width = 28;
    reasonsSheet.getCell(1, 1).value = "Allowed Reasons";
    reasonsSheet.getCell(1, 1).font = { bold: true };
    SALARY_REVISION_REASONS.forEach((r, i) => {
      reasonsSheet.getCell(i + 2, 1).value = r;
    });

    const yesNoSheet = wb.addWorksheet("YesNo");
    yesNoSheet.getColumn(1).width = 10;
    yesNoSheet.getCell(1, 1).value = "Continue";
    yesNoSheet.getCell(1, 1).font = { bold: true };
    CONTINUE_YES_NO.forEach((t, i) => {
      yesNoSheet.getCell(i + 2, 1).value = t;
    });

    const sheet = wb.addWorksheet("Revisions");
    manualHeaders.forEach((h, i) => {
      const cell = sheet.getCell(1, i + 1);
      cell.value = h;
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE8EEF5" },
      };
      sheet.getColumn(i + 1).width = i === 1 ? 24 : 16;
    });

    const seed = seedEmployees.slice(0, 500);
    const nManual = manualComponents.length;
    const revisedCol = 4 + nManual;
    const dateCol = 5 + nManual;
    const payCol = 6 + nManual;
    const continueCol = 7 + nManual;
    const reasonCol = 8 + nManual;
    const sampleEffective = allowMidMonth ? "01/07/2026" : "2026-07";

    if (seed.length) {
      seed.forEach((emp, idx) => {
        const r = idx + 2;
        sheet.getCell(r, 1).value = emp.empCode || emp.employeeCode || "";
        sheet.getCell(r, 2).value = employeeDisplayName(emp);
        sheet.getCell(r, 3).value = Number(emp.ctc || emp.ctcAmount || 0) || null;
        manualComponents.forEach((comp, ci) => {
          const def = Number(comp.manualAmount);
          sheet.getCell(r, 4 + ci).value =
            Number.isFinite(def) && def > 0 ? def : null;
        });
        sheet.getCell(r, revisedCol).value = Number(emp.ctc || emp.ctcAmount || 0) || null;
        sheet.getCell(r, dateCol).value = "";
        sheet.getCell(r, dateCol).numFmt = "@";
        sheet.getCell(r, payCol).value = defaultPayMonth;
        sheet.getCell(r, payCol).numFmt = "@";
        sheet.getCell(r, continueCol).value = "No";
        sheet.getCell(r, reasonCol).value = "";
      });
    } else {
      sheet.getCell(2, 1).value = "MK0001";
      sheet.getCell(2, 2).value = "Sample Manual Employee";
      sheet.getCell(2, 3).value = 600000;
      sheet.getCell(2, revisedCol).value = 720000;
      sheet.getCell(2, dateCol).value = sampleEffective;
      sheet.getCell(2, dateCol).numFmt = "@";
      sheet.getCell(2, payCol).value = defaultPayMonth;
      sheet.getCell(2, payCol).numFmt = "@";
      sheet.getCell(2, continueCol).value = "No";
      sheet.getCell(2, reasonCol).value = "Performance";
    }

    for (let r = 2; r <= Math.max(seed.length + 1, 200); r++) {
      sheet.getCell(r, dateCol).numFmt = "@";
      sheet.getCell(r, payCol).numFmt = "@";
      sheet.getCell(r, continueCol).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`YesNo!$A$2:$A$${CONTINUE_YES_NO.length + 1}`],
        showErrorMessage: true,
        errorTitle: "Invalid value",
        error: "Select Yes or No",
      };
      sheet.getCell(r, reasonCol).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`Reasons!$A$2:$A$${SALARY_REVISION_REASONS.length + 1}`],
        showErrorMessage: true,
        errorTitle: "Invalid Reason",
        error: "Select a reason from the list",
      };
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Salary_Revision_Manual_${String(selectedStructure.name || "Import")
      .replace(/[^\w.-]+/g, "_")
      .slice(0, 40)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = async () => {
    if (!selectedStructure) {
      toast.error("Select a salary structure first");
      return;
    }
    try {
      if (isManualStructure) {
        await downloadManualTemplate();
      } else {
        await downloadAutoTemplate();
      }
      toast.success(
        `Template downloaded (${structureModeLabel}${
          seedEmployees.length
            ? ` · ${seedEmployees.length} employee row${seedEmployees.length === 1 ? "" : "s"}`
            : ""
        })`
      );
    } catch (err) {
      toast.error(err?.message || "Failed to download template");
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = "";
    if (!file) return;

    if (!selectedStructure) {
      toast.error("Select a salary structure first");
      return;
    }
    if (isManualStructure && !manualComponents.length) {
      toast.error("Selected structure has no Manual fixed components");
      return;
    }

    setBusy(true);
    setImportResult(null);
    setSourceFile(file);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array", cellDates: true });
      const sheetName = pickRevisionsSheetName(workbook.SheetNames);
      const worksheet = sheetName ? workbook.Sheets[sheetName] : null;
      const json = worksheet
        ? XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" })
        : [];
      if (!json.length) {
        toast.error("The Excel file appears empty");
        setPreviewRows([]);
        setReview(null);
        return;
      }

      const headers = json[0];
      const fileLooksManual = headersLookManual(headers);
      if (isManualStructure && !fileLooksManual) {
        toast.error(
          `Structure "${selectedStructure.name}" is Manual — upload the Manual revision template (component columns + Revised Annual CTC), not Amount/%age.`
        );
        setPreviewRows([]);
        setReview(null);
        return;
      }
      if (!isManualStructure && fileLooksManual) {
        toast.error(
          `Structure "${selectedStructure.name}" is Auto (Formula) — upload the Amount/%age revision template.`
        );
        setPreviewRows([]);
        setReview(null);
        return;
      }

      const mapped = [];
      for (let i = 1; i < json.length; i++) {
        const raw = json[i];
        if (!raw || raw.every((c) => c === "" || c == null)) continue;
        const row = isManualStructure
          ? mapManualRowFromHeaders(
              headers,
              raw,
              i + 1,
              manualComponents,
              currentCtcByCode
            )
          : mapAutoRowFromHeaders(headers, raw, i + 1, currentCtcByCode);

        // Skip untouched template seed stubs (emp prefilled, revision fields blank).
        const hasRevisionInput = isManualStructure
          ? (Number.isFinite(row.newCtc) &&
              Number.isFinite(row.currentCtc) &&
              Math.abs(Number(row.newCtc) - Number(row.currentCtc)) >= 1) ||
            !!row.effectiveDate ||
            !!row.reason ||
            (row.manualOverrides &&
              Object.values(row.manualOverrides).some(
                (v) => Number.isFinite(Number(v)) && Number(v) !== 0
              ))
          : (Number.isFinite(row.revisionValue) && row.revisionValue !== 0) ||
            !!row.effectiveDate ||
            !!row.reason ||
            (Number.isFinite(row.newCtc) &&
              Number.isFinite(row.currentCtc) &&
              Math.abs(Number(row.newCtc) - Number(row.currentCtc)) >= 1);

        if (
          !row.empCode &&
          !row.revisionValue &&
          !row.newCtc &&
          !row.effectiveDate &&
          !row.reason &&
          !(row.manualOverrides && Object.keys(row.manualOverrides).length)
        ) {
          continue;
        }
        if (row.empCode && !hasRevisionInput) {
          continue;
        }
        const reasonMatch = SALARY_REVISION_REASONS.find(
          (r) =>
            r.toLowerCase() ===
            String(row.reason || "")
              .trim()
              .replace(/\s+/g, " ")
              .toLowerCase()
        );
        if (reasonMatch) row.reason = reasonMatch;
        row.clientErrors = isManualStructure
          ? clientValidateManualBase(row, allowMidMonth, finalizedPayrollMonthSet)
          : clientValidateAuto(row, allowMidMonth, finalizedPayrollMonthSet);
        row.employeeName = row.employeeName || row.name || "";
        mapped.push(row);
      }

      if (!mapped.length) {
        toast.error("No data rows found. Use the Revisions sheet with the template headers.");
        setPreviewRows([]);
        setReview(null);
        return;
      }
      setPreviewRows(mapped);
      const built = buildReviewFromPreview(
        mapped,
        isManualStructure,
        selectedStructure
      );
      setReview(built);
      toast.success(
        `Validated: ${built.successCount} successful, ${built.failedCount} failed`
      );
    } catch (err) {
      toast.error(err?.message || "Failed to read Excel file");
      setPreviewRows([]);
      setReview(null);
    } finally {
      setBusy(false);
    }
  };

  const runImport = async ({ ignoreFailed }) => {
    if (!review) return;
    if (!ignoreFailed && review.failedCount > 0) {
      toast.error("Fix failed rows or choose Ignore failed & import.");
      return;
    }
    const successRows = Array.isArray(review.successRows) ? review.successRows : [];
    if (!successRows.length) {
      toast.error("No successful rows to import");
      return;
    }
    setBusy(true);
    try {
      const payload = successRows.map((row) => {
        const r = row.record || {};
        return {
          rowNumber: row.row || r.rowNumber,
          empCode: row.code || r.empCode,
          newCtc: r.newCtc,
          revisionType: r.revisionType || null,
          revisionValue: r.revisionValue,
          effectiveDate: r.effectiveDate,
          bookingPayMonth: r.payMonth || r.bookingPayMonth || null,
          payMonth: r.payMonth || r.bookingPayMonth || null,
          confirmSameEffectiveContinue: r.continueSameMonthIncrement ? "Yes" : "No",
          continueSameMonthIncrement: r.continueSameMonthIncrement ? "Yes" : "No",
          reason: r.reason,
          remarks: r.remarks,
          ...(r.manualOverrides ? { manualOverrides: r.manualOverrides } : {}),
          ...(r.structureSnapshot ? { structureSnapshot: r.structureSnapshot } : {}),
        };
      });
      const res = await salaryService.bulkImportSalaries(payload);
      const data = res?.data || res;
      setImportResult(data);
      const ok = Number(data?.successCount) || 0;
      const fail = Number(data?.errorCount) || 0;
      if (ok > 0) {
        toast.success(`Imported ${ok} revision(s)${fail ? `, ${fail} failed` : ""}`);
        const pendingManual = (data?.results || []).filter(
          (r) =>
            r?.structureRefresh?.pendingManual ||
            r?.structureRefresh?.reason === "manual_breakdown_missing"
        ).length;
        if (pendingManual > 0) {
          toast(
            `${pendingManual} Manual employee(s) still need component amounts (Profile → Salary Structure or Structure component values).`,
            { icon: "⚠️", duration: 8000 }
          );
        }
        onImported?.(data);
        setReview(null);
        setPreviewRows([]);
        setSourceFile(null);
      } else {
        toast.error(data?.message || "No rows imported");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!review || !sourceFile) {
      toast.error("Validate a file first.");
      return;
    }
    try {
      setBusy(true);
      await downloadValidationStatusWorkbook(
        sourceFile,
        review.allRows,
        "Salary_Revision_Validation_Status.xlsx",
        { sheetName: "Revisions" }
      );
      toast.success("Validation report downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to download report");
    } finally {
      setBusy(false);
    }
  };

  const panel = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";

  const inputCls = `w-full rounded border px-3 py-2 text-sm ${
    darkMode
      ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
      : "border-[hsl(var(--ds-border))]"
  }`;

  return createPortal(
    <div className="fixed inset-0 z-[10030] flex items-center justify-center bg-black/45 p-4">
      <div
        className={`flex max-h-[92vh] w-[min(100%,980px)] flex-col overflow-hidden rounded-xl border shadow-2xl ${panel}`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">Import Salary Revisions</h3>
            <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
              Select a salary structure — the product detects Manual vs Auto (Formula) and
              provides the matching template. Include Pay Month ({EXCEL_MONTH_FORMAT}) and
              {allowMidMonth
                ? ` Increment Effective Date (${EXCEL_DATE_FORMAT}).`
                : ` Increment Effective Month (${EXCEL_MONTH_FORMAT}).`}
              {" "}
              Continue Same Month Increment = Yes stacks another increment on the same effective month.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1 opacity-70 hover:opacity-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-xs font-medium">
              Salary structure <span className="text-red-500">*</span>
              <select
                className={`mt-1 ${inputCls}`}
                value={templateId}
                disabled={busy || !!review}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  setReview(null);
                  setImportResult(null);
                  setPreviewRows([]);
                  setSourceFile(null);
                }}
              >
                <option value="">Select structure…</option>
                {(structures || [])
                  .filter((s) => s.active !== false)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {structureRequiresManualInput(s)
                        ? " · Manual"
                        : " · Auto (Formula)"}
                    </option>
                  ))}
              </select>
            </label>
            {selectedStructure ? (
              <div className="flex flex-col justify-end">
                <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                  Detected mode:{" "}
                  <span
                    className={
                      isManualStructure
                        ? "font-semibold text-[hsl(var(--ds-warning))]"
                        : "font-semibold text-[hsl(var(--ds-primary))]"
                    }
                  >
                    {structureModeLabel}
                  </span>
                  {" · "}
                  Seed rows: {seedEmployees.length}
                  {isManualStructure
                    ? ` · Manual heads: ${manualComponents.length}`
                    : " · Template: Amount / %age"}
                </p>
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <DsButton
              type="button"
              variant="outline"
              onClick={downloadTemplate}
              disabled={busy || !selectedStructure}
            >
              <Download size={14} />
              Download template
            </DsButton>
            <DsButton
              type="button"
              variant="secondary"
              onClick={() => fileRef.current?.click()}
              disabled={busy || !selectedStructure}
            >
              <FileUp size={14} />
              Choose Excel file
            </DsButton>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".xlsx,.xls,.csv"
              onChange={handleFile}
            />
          </div>

          <div className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3 text-[11px] text-[hsl(var(--ds-muted-fg))]">
            <p className="font-semibold text-[hsl(var(--ds-fg))]">Required columns</p>
            {!selectedStructure ? (
              <p className="mt-1">Select a salary structure to see the template columns.</p>
            ) : isManualStructure ? (
              <p className="mt-1">
                Employee Code · Manual monthly (
                {manualComponents.map((c) => manualFixedColumnLabel(c)).join(", ") || "—"}
                ) · Revised Annual CTC ·{" "}
                {allowMidMonth
                  ? `Increment Effective Date (${EXCEL_DATE_FORMAT})`
                  : `Increment Effective Month (${EXCEL_MONTH_FORMAT})`}{" "}
                · Pay Month ({EXCEL_MONTH_FORMAT}) · Continue Same Month Increment (Yes/No) ·
                Reason ({SALARY_REVISION_REASONS.join(", ")})
              </p>
            ) : (
              <p className="mt-1">
                Employee Code · Current CTC · Revision Type (Amount = increment /
                %age) · Revision Value ·{" "}
                {allowMidMonth
                  ? `Increment Effective Date (${EXCEL_DATE_FORMAT})`
                  : `Increment Effective Month (${EXCEL_MONTH_FORMAT})`}{" "}
                · Pay Month ({EXCEL_MONTH_FORMAT}) · Continue Same Month Increment (Yes/No) ·
                Reason ({SALARY_REVISION_REASONS.join(", ")})
              </p>
            )}
          </div>

          {review ? (
            <div className="min-h-[320px]">
              <ImportValidationReview
                result={review}
                busy={busy}
                title="Salary revision validation"
                entityLabel="revision"
                onDownloadReport={handleDownloadReport}
                onImportAll={() => runImport({ ignoreFailed: false })}
                onImportSuccessfulOnly={() => runImport({ ignoreFailed: true })}
                onBack={() => {
                  setReview(null);
                  setPreviewRows([]);
                  setSourceFile(null);
                }}
              />
            </div>
          ) : null}

          {importResult && (
            <div className="rounded-lg border border-[hsl(var(--ds-border))] p-3 text-xs">
              <p className="font-semibold">
                Import result: {importResult.successCount || 0} imported,{" "}
                {importResult.errorCount || 0} failed
              </p>
              {(importResult.results || []).filter((r) => !r.success).length > 0 && (
                <ul className="mt-2 max-h-32 space-y-1 overflow-y-auto text-[hsl(var(--ds-danger))]">
                  {(importResult.results || [])
                    .filter((r) => !r.success)
                    .slice(0, 30)
                    .map((r) => (
                      <li key={`${r.rowNumber}-${r.empCode}`}>
                        Row {r.rowNumber} ({r.empCode || "—"}): {r.message}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {!review ? (
          <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
            <DsButton type="button" variant="outline" onClick={onClose} disabled={busy}>
              Close
            </DsButton>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
